import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import { orgsAtom, store } from "~/atoms";
import { consumeBuildChange } from "~/lib/appBuild";
import {
  type RefreshCurrentOrgAccessResult,
  refreshCurrentOrgAccess,
} from "~/lib/orgSelection";
import { Env } from "~/utils/Env";

export type BuildChangeRefreshStatus =
  /** No refresh owed — render as usual. */
  | "idle"
  /** Access is being re-fetched; hold the routed page behind a loader. */
  | "refreshing"
  /** Refreshed (or gave up and kept the cached access) — render. */
  | "done"
  /** The cached org is no longer granted — send the user back to Login. */
  | "redirect";

/**
 * Module-scope single flight: StrictMode double-mounts and the two gates that
 * call this (one per auth path) must never produce two refresh sequences for
 * one page load.
 */
let refreshInFlight: Promise<RefreshCurrentOrgAccessResult | "failed"> | null =
  null;

function refreshOnce(): Promise<RefreshCurrentOrgAccessResult | "failed"> {
  if (!refreshInFlight) {
    refreshInFlight = refreshCurrentOrgAccess().catch((err: unknown) => {
      // Keep the cached access rather than block the page: stale entitlements
      // are the status quo this feature improves on, not a reason to lock out.
      Sentry.captureException(err, {
        tags: { event: "app.build_change_access_refresh.failure" },
      });
      return "failed" as const;
    });
  }
  return refreshInFlight;
}

/**
 * Re-fetch the cached access data once per page load when the running build
 * differs from the one this browser last booted (INS-1968). The boot trusts
 * the localStorage copy of orgs/roles/permissions on protected routes, so
 * without this a release that adds a package or permission stays invisible
 * until the next login. Decided at mount — a boot that found no cache fetches
 * from scratch anyway and owes nothing here.
 *
 * `enabled` tells the hook the session is usable (WorkOS boot ready, Zitadel
 * token present); the refresh waits for it.
 */
export function useBuildChangeAccessRefresh(
  enabled: boolean,
): BuildChangeRefreshStatus {
  const [needed] = useState<boolean>(
    () =>
      Env.VITE_MOCK !== "true" &&
      consumeBuildChange() &&
      store.get(orgsAtom)?.some((o) => o?.isCurrent) === true,
  );
  const [status, setStatus] = useState<BuildChangeRefreshStatus>(
    needed ? "refreshing" : "idle",
  );

  useEffect(() => {
    if (!needed || !enabled) return;
    let cancelled = false;
    void refreshOnce().then((result) => {
      if (cancelled) return;
      setStatus(result === "current-org-removed" ? "redirect" : "done");
    });
    return () => {
      cancelled = true;
    };
  }, [needed, enabled]);

  return status;
}

/** Test-only: drop the single-flight promise between cases. */
export function __resetBuildChangeAccessRefreshForTests(): void {
  refreshInFlight = null;
}
